import {
  CalendarMonthOutlined,
  RunningWithErrorsOutlined,
  SearchOffOutlined,
  TimerOutlined,
  TouchApp,
} from "@mui/icons-material";
import { Box } from "@mui/material";
import React, { lazy, Suspense, useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import AnalyticsCard from "@/components/common/AnalyticsCard";
import { CardSkeleton } from "@/components/common/CardSkeleton";
import { LongRunningDataLoader } from "@/components/common/LongRunningDataLoader";
import { getReportNetworkLoading } from "@/hooks/getReportNetworkLoading";
import {
  type ClickAndCollectChartMetric,
  type ClickAndCollectReportDay,
  useClickAndCollectReport,
} from "@/hooks/useClickAndCollectReport";
import { downloadCsv } from "@/pages/reports/shared/storeInsightsCsv";
import { StoreInsightsReportHeader } from "@/pages/reports/shared/StoreInsightsReportHeader";
import { TabbedReportChartCard } from "@/pages/reports/shared/TabbedReportChartCard";
import { useStoreInsightsReportFilters } from "@/pages/reports/shared/useStoreInsightsReportFilters";
import { shouldShowInsightsTrends } from "@/pages/store-insights/shared/insightsFeatureGates";
import { useStoreInsightsTrendComparison } from "@/pages/store-insights/shared/useStoreInsightsTrendComparison";
import { formatTrendChange } from "@/utils/analyticsTrend";
import { formatInteger, formatPercentFromWhole } from "@/utils/number";
import {
  formatStoreDate,
  formatStoreDateKey,
  formatStoreDateRange,
  getBrowserTimezone,
} from "@/utils/storeTime";
import { formatDurationShort } from "@/utils/time";

const ClickAndCollectOverTimeChart = lazy(
  () => import("@/pages/admin/experimental/click-and-collect/ClickAndCollectOverTimeChart"),
);

const chartMetricKeys: ClickAndCollectChartMetric[] = [
  "requests",
  "responseTime",
  "missedPercentage",
];

const sum = (values: number[]) => values.reduce((total, value) => total + value, 0);

const getMetricValue = (days: ClickAndCollectReportDay[], metric: ClickAndCollectChartMetric) => {
  const requests = sum(days.map((day) => day.requests));
  const missedRequests = sum(days.map((day) => day.missedRequests));
  const totalTimeTakenSeconds = sum(days.map((day) => day.totalTimeTakenSeconds));

  switch (metric) {
    case "responseTime":
      return requests > 0 ? totalTimeTakenSeconds / requests : null;
    case "missedPercentage":
      return requests > 0 ? (missedRequests / requests) * 100 : null;
    case "requests":
    default:
      return requests;
  }
};

const getDailyMetricValue = (day: ClickAndCollectReportDay, metric: ClickAndCollectChartMetric) => {
  switch (metric) {
    case "responseTime":
      return day.requests > 0 ? day.totalTimeTakenSeconds / day.requests : 0;
    case "missedPercentage":
      return day.requests > 0 ? (day.missedRequests / day.requests) * 100 : 0;
    case "requests":
    default:
      return day.requests;
  }
};

const ClickAndCollectReport: React.FC = () => {
  const { t, i18n } = useTranslation();
  const [metric, setMetric] = useState<ClickAndCollectChartMetric>("requests");
  const {
    user,
    isCraveUser,
    selectedStores,
    loading,
    period,
    customRange,
    currentDateRange,
    deviceType,
    deviceTypeFilterDisabled,
    customers,
    selectedCustomerIds,
    groups,
    selectedGroups,
    groupCounts,
    filteredStores,
    onCustomerChange,
    onGroupsChange,
    onStoresChange,
    onPeriodChange,
    onCustomRangeChange,
  } = useStoreInsightsReportFilters({
    enableDeviceFilter: false,
    defaultPeriod: "last_4_weeks",
  });
  const {
    comparisonWindow,
    setComparisonWindow,
    anchorComparisonToWeekdays,
    setAnchorComparisonToWeekdays,
  } = useStoreInsightsTrendComparison(period, currentDateRange);
  const {
    comparisonRange,
    data: reportData,
    error,
    loading: reportLoading,
  } = useClickAndCollectReport({
    stores: selectedStores.length ? selectedStores : null,
    currentRange: currentDateRange,
    comparisonWindow,
    anchorComparisonToWeekdays:
      (period === "last_4_weeks" || period === "custom") &&
      (comparisonWindow === "month_before" || comparisonWindow === "year_before")
        ? anchorComparisonToWeekdays
        : true,
    deviceType: "all",
  });
  const currentLanguage = i18n.resolvedLanguage || i18n.language || "en-US";
  const locale = useMemo(() => new Intl.Locale(currentLanguage), [currentLanguage]);
  const browserTimezone = getBrowserTimezone();
  const networkLoading = getReportNetworkLoading(loading, reportLoading);
  const selectionLoading = !selectedStores.length && filteredStores.length > 0;
  const showTrends = shouldShowInsightsTrends(isCraveUser);
  const chartLoading = loading || selectionLoading || reportLoading;
  const chartLoadingKey = useMemo(
    () =>
      [
        selectedStores.map((store) => store.id).join(","),
        currentDateRange.start.getTime(),
        currentDateRange.end.getTime(),
      ].join("|"),
    [currentDateRange.end, currentDateRange.start, selectedStores],
  );
  const currentRequests = sum(reportData.current.map((day) => day.requests));
  const hasNoData = !chartLoading && !error && currentRequests === 0;
  const isSingleDayRange =
    formatStoreDateKey(currentDateRange.start, browserTimezone) ===
    formatStoreDateKey(currentDateRange.end, browserTimezone);
  const trendComparisonRange = formatStoreDateRange(
    comparisonRange.start,
    comparisonRange.end,
    browserTimezone,
    currentLanguage,
    {
      weekday: "short",
      month: "short",
      day: "numeric",
    },
  );
  const trendComparisonRangeLabel = `${trendComparisonRange.startDate} – ${trendComparisonRange.endDate}`;
  const summary = useMemo(() => {
    const trend = (
      current: number | null,
      previous: number | null,
      lowerIsBetter: boolean,
      formatValue: (value: number) => string,
    ) => {
      if (current === null || previous === null) return { status: "unavailable" as const };
      if (previous === 0) {
        const direction = current === 0 ? ("flat" as const) : ("up" as const);
        const result = {
          status: "ready" as const,
          absoluteChange: current === 0 ? undefined : current,
          previousValue: previous,
          direction,
          isGood: direction === "flat" || !lowerIsBetter,
        };
        return {
          ...result,
          text: formatTrendChange(
            result,
            formatValue,
            (value) => formatPercentFromWhole(value, locale),
            t("clickAndCollectReport.trends.noChange"),
          ),
          tooltip: t("common.analytics.trends.tooltip", {
            dateRange: trendComparisonRangeLabel,
            previousValue: formatValue(previous),
          }),
        };
      }
      const difference = ((current - previous) / Math.abs(previous)) * 100;
      const direction =
        difference === 0 ? ("flat" as const) : difference > 0 ? ("up" as const) : ("down" as const);
      const result = {
        status: "ready" as const,
        percent: Math.abs(difference),
        previousValue: previous,
        direction,
        isGood: direction === "flat" || (lowerIsBetter ? direction === "down" : direction === "up"),
      };
      return {
        ...result,
        text: formatTrendChange(
          result,
          formatValue,
          (value) => formatPercentFromWhole(value, locale),
          t("clickAndCollectReport.trends.noChange"),
        ),
        tooltip: t("common.analytics.trends.tooltip", {
          dateRange: trendComparisonRangeLabel,
          previousValue: formatValue(previous),
        }),
      };
    };
    const requests = getMetricValue(reportData.current, "requests");
    const responseTime = getMetricValue(reportData.current, "responseTime");
    const missedPercentage = getMetricValue(reportData.current, "missedPercentage");
    const previousRequests = getMetricValue(reportData.comparison, "requests");
    const previousResponseTime = getMetricValue(reportData.comparison, "responseTime");
    const previousMissedPercentage = getMetricValue(reportData.comparison, "missedPercentage");
    const chartMetricValue = getMetricValue(reportData.current, metric);
    const previousChartMetricValue = getMetricValue(reportData.comparison, metric);

    return {
      requests,
      responseTime,
      missedPercentage,
      requestsTrend: trend(requests, previousRequests, false, (value) =>
        formatInteger(value, locale),
      ),
      responseTimeTrend: trend(responseTime, previousResponseTime, true, (value) =>
        formatDurationShort(value, locale),
      ),
      missedPercentageTrend: trend(missedPercentage, previousMissedPercentage, true, (value) =>
        formatPercentFromWhole(value, locale),
      ),
      chartMetricTrend: trend(
        chartMetricValue,
        previousChartMetricValue,
        metric === "responseTime" || metric === "missedPercentage",
        (value) => {
          switch (metric) {
            case "responseTime":
              return formatDurationShort(value, locale);
            case "missedPercentage":
              return formatPercentFromWhole(value, locale);
            case "requests":
            default:
              return formatInteger(value, locale);
          }
        },
      ),
    };
  }, [locale, metric, reportData, t, trendComparisonRangeLabel]);

  const currentRangeSpansYears =
    formatStoreDateKey(currentDateRange.start, browserTimezone).slice(0, 4) !==
    formatStoreDateKey(currentDateRange.end, browserTimezone).slice(0, 4);
  const comparisonRangeSpansYears =
    formatStoreDateKey(comparisonRange.start, browserTimezone).slice(0, 4) !==
    formatStoreDateKey(comparisonRange.end, browserTimezone).slice(0, 4);
  const showRangeYears = currentRangeSpansYears || comparisonRangeSpansYears;

  const chartData = useMemo(
    () =>
      reportData.current.map((currentDay, index) => {
        const comparisonDay = reportData.comparison[index];
        return {
          label: formatStoreDate(currentDay.date, browserTimezone, currentLanguage, {
            month: "short",
            day: "numeric",
            year: showRangeYears ? "numeric" : undefined,
          }),
          currentDateLabel: formatStoreDate(currentDay.date, browserTimezone, currentLanguage, {
            weekday: "short",
            month: "short",
            day: "numeric",
            year: showRangeYears ? "numeric" : undefined,
          }),
          comparisonDateLabel: comparisonDay
            ? formatStoreDate(comparisonDay.date, browserTimezone, currentLanguage, {
                weekday: "short",
                month: "short",
                day: "numeric",
                year: showRangeYears ? "numeric" : undefined,
              })
            : "–",
          current: getDailyMetricValue(currentDay, metric),
          comparison: comparisonDay ? getDailyMetricValue(comparisonDay, metric) : 0,
        };
      }),
    [browserTimezone, currentLanguage, metric, reportData, showRangeYears],
  );
  const selectedRange = formatStoreDateRange(
    currentDateRange.start,
    currentDateRange.end,
    browserTimezone,
    currentLanguage,
    {
      weekday: "short",
      month: "short",
      day: "numeric",
      year: showRangeYears ? "numeric" : undefined,
    },
  );
  const selectedRangeLabel = `${selectedRange.startDate} – ${selectedRange.endDate}`;
  const comparisonRangeDisplay = formatStoreDateRange(
    comparisonRange.start,
    comparisonRange.end,
    browserTimezone,
    currentLanguage,
    {
      weekday: "short",
      month: "short",
      day: "numeric",
      year: showRangeYears ? "numeric" : undefined,
    },
  );
  const comparisonRangeLabel = `${comparisonRangeDisplay.startDate} – ${comparisonRangeDisplay.endDate}`;
  const chartMetricLabel = t(`clickAndCollectReport.metrics.${metric}`);
  const chartTitle = t("clickAndCollectReport.sections.metricOverTime", {
    metric: chartMetricLabel,
  });
  const handleExport = useCallback(() => {
    const exportRows = [
      ...reportData.current.map((day) => [day.date, getDailyMetricValue(day, metric)]),
      ...reportData.comparison.map((day) => [day.date, getDailyMetricValue(day, metric)]),
    ].sort(([leftDate], [rightDate]) => String(leftDate).localeCompare(String(rightDate)));

    downloadCsv(
      [[t("clickAndCollectReport.export.date"), chartMetricLabel], ...exportRows],
      `click_and_collect_over_time_${metric}_${formatStoreDateKey(currentDateRange.start, browserTimezone)}.csv`,
    );
  }, [browserTimezone, chartMetricLabel, currentDateRange.start, metric, reportData, t]);
  const renderTrendTitle = () => {
    if (!showTrends) return null;
    if (chartLoading) {
      return <CardSkeleton width={118} sx={{ height: "0.75rem", transform: "none" }} />;
    }
    if (hasNoData || summary.chartMetricTrend.status !== "ready") return null;
    const { direction, isGood, text } = summary.chartMetricTrend;
    return (
      <Box
        component="span"
        sx={{
          color: direction === "flat" ? "text.secondary" : isGood ? "success.main" : "warning.main",
          fontSize: 12,
          fontWeight: 600,
          "&::before": {
            content: direction === "up" ? '"▲"' : direction === "down" ? '"▼"' : '"="',
            fontSize: 9,
            marginInlineEnd: "4px",
          },
        }}
      >
        {t("clickAndCollectReport.trends.vsPreviousPeriod", { value: text })}
      </Box>
    );
  };

  return (
    <Box sx={{ pb: 4 }}>
      <StoreInsightsReportHeader
        title={t("clickAndCollectReport.title")}
        icon={<TouchApp />}
        isCraveUser={isCraveUser}
        currentUserRole={user?.role}
        customers={customers}
        selectedCustomerIds={selectedCustomerIds}
        onCustomerChange={onCustomerChange}
        groups={groups}
        selectedGroups={selectedGroups}
        groupCounts={groupCounts}
        onGroupsChange={onGroupsChange}
        filteredStores={filteredStores}
        selectedStores={selectedStores}
        onStoresChange={onStoresChange}
        loading={loading}
        networkLoading={networkLoading}
        period={period}
        currentDateRange={currentDateRange}
        customRange={customRange}
        onPeriodChange={onPeriodChange}
        onCustomRangeChange={onCustomRangeChange}
        deviceTypeFilterEnabled
        deviceTypeFilterDisabled
        selectedDeviceType="all"
        showTrendSelector
        trendPeriod={comparisonWindow}
        onTrendPeriodChange={setComparisonWindow}
        trendComparisonRange={comparisonRange}
        anchorTrendComparisonToWeekdays={anchorComparisonToWeekdays}
        onAnchorTrendComparisonToWeekdaysChange={setAnchorComparisonToWeekdays}
      />

      <Box sx={{ p: 2.5, pt: 0, mt: 3 }}>
        <Box component="h2" sx={{ fontSize: 20, fontWeight: 600, mb: 2 }}>
          {t("clickAndCollectReport.sections.summary")}
        </Box>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "repeat(3, 1fr)" },
            gap: 2,
            mb: 3,
          }}
        >
          <AnalyticsCard
            title={t("clickAndCollectReport.cards.requests")}
            icon={<TouchApp />}
            value={
              chartLoading || summary.requests === null || summary.requests === 0
                ? "--"
                : formatInteger(summary.requests, locale)
            }
            previousText={null}
            isPositive
            subtitle={t("clickAndCollectReport.cards.requestsSubtitle")}
            pillText={null}
            showGradient
            trend={
              showTrends
                ? chartLoading
                  ? { status: "loading" }
                  : hasNoData
                    ? undefined
                    : summary.requestsTrend
                : undefined
            }
          />
          <AnalyticsCard
            title={t("clickAndCollectReport.cards.responseTime")}
            icon={<TimerOutlined />}
            value={
              chartLoading || summary.responseTime === null
                ? "--"
                : formatDurationShort(summary.responseTime, locale)
            }
            previousText={null}
            isPositive
            subtitle={t("clickAndCollectReport.cards.responseTimeSubtitle")}
            pillText={null}
            showGradient
            trend={
              showTrends
                ? chartLoading
                  ? { status: "loading" }
                  : hasNoData
                    ? undefined
                    : summary.responseTimeTrend
                : undefined
            }
          />
          <AnalyticsCard
            title={t("clickAndCollectReport.cards.missedPercentage")}
            icon={<RunningWithErrorsOutlined />}
            value={
              chartLoading || summary.missedPercentage === null
                ? "--"
                : formatPercentFromWhole(summary.missedPercentage, locale)
            }
            previousText={null}
            isPositive
            subtitle={t("clickAndCollectReport.cards.missedPercentageSubtitle")}
            pillText={null}
            showGradient
            trend={
              showTrends
                ? chartLoading
                  ? { status: "loading" }
                  : hasNoData
                    ? undefined
                    : summary.missedPercentageTrend
                : undefined
            }
          />
        </Box>

        <TabbedReportChartCard
          title={
            isSingleDayRange ? null : (
              <Box
                component="span"
                sx={{ display: "inline-flex", alignItems: "baseline", gap: 1.25 }}
              >
                <Box component="span">{chartTitle}</Box>
                {renderTrendTitle()}
              </Box>
            )
          }
          onExport={isSingleDayRange ? undefined : handleExport}
          exportDisabled={reportLoading || reportData.current.length === 0}
          tabs={chartMetricKeys.map((key) => ({
            value: key,
            label: t(`clickAndCollectReport.metrics.${key}`),
          }))}
          selectedTab={metric}
          onTabChange={(value) => setMetric(value as ClickAndCollectChartMetric)}
          tabsAriaLabel={t("clickAndCollectReport.fields.metric")}
          showTabs={!isSingleDayRange}
          legendItems={[
            { label: selectedRangeLabel },
            { label: comparisonRangeLabel, lineStyle: "dashed", color: "#98A2B3" },
          ]}
          loadingOverlay={
            !isSingleDayRange && chartLoading ? (
              <LongRunningDataLoader
                variant="focused"
                isLoading={true}
                baseLabels={[
                  t("clickAndCollectReport.loading"),
                  t("storeInsights.common.loading.readingActivity"),
                  t("storeInsights.common.loading.comparingPeriods"),
                  t("storeInsights.common.loading.calculatingInsights"),
                  t("storeInsights.common.loading.preparingChart"),
                ]}
                loopingLabels={[
                  t("storeInsights.common.loading.stillWorking"),
                  t("storeInsights.common.loading.thanksForPatience"),
                ]}
                fixedDurationMs={30_000}
                locale={currentLanguage}
                loadingKey={chartLoadingKey}
                estimateLabel={(duration) =>
                  t("storeComparison.loading.usuallyUnder", { duration })
                }
                containerSx={{
                  position: "absolute",
                  inset: 0,
                  minHeight: 0,
                  zIndex: 1,
                }}
              />
            ) : undefined
          }
        >
          {isSingleDayRange ? (
            <Box
              sx={{
                minHeight: 320,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                textAlign: "center",
                color: "text.secondary",
              }}
            >
              <CalendarMonthOutlined sx={{ fontSize: 48, color: "#98A2B3", mb: 2 }} />
              <Box sx={{ color: "text.primary", fontSize: 22, fontWeight: 700 }}>
                {t("clickAndCollectReport.empty.singleDayTitle")}
              </Box>
              <Box sx={{ mt: 1.5, maxWidth: 440, fontSize: 17, lineHeight: 1.5 }}>
                {t("clickAndCollectReport.empty.singleDayDescription")}
              </Box>
            </Box>
          ) : (
            <Box sx={{ position: "relative" }}>
              {chartLoading ? (
                <Box aria-hidden="true" sx={{ height: 360 }} />
              ) : hasNoData ? (
                <Box
                  sx={{
                    height: 360,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    textAlign: "center",
                    color: "text.secondary",
                  }}
                >
                  <SearchOffOutlined sx={{ fontSize: 48, color: "#98A2B3", mb: 2 }} />
                  <Box sx={{ color: "text.primary", fontSize: 22, fontWeight: 700 }}>
                    {t("clickAndCollectReport.empty.noDataTitle")}
                  </Box>
                  <Box sx={{ mt: 1.5, maxWidth: 440, fontSize: 17, lineHeight: 1.5 }}>
                    {t("clickAndCollectReport.empty.noDataDescription")}
                  </Box>
                </Box>
              ) : (
                <Suspense fallback={<Box sx={{ height: 360 }} />}>
                  <ClickAndCollectOverTimeChart
                    data={chartData}
                    locale={locale}
                    metric={metric}
                    currentLabel={selectedRangeLabel}
                    comparisonLabel={comparisonRangeLabel}
                  />
                </Suspense>
              )}
              {error ? (
                <Box
                  sx={{
                    position: "absolute",
                    inset: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "text.secondary",
                  }}
                >
                  {t("clickAndCollectReport.errors.failedLoad")}
                </Box>
              ) : null}
            </Box>
          )}
        </TabbedReportChartCard>
      </Box>
    </Box>
  );
};

export default ClickAndCollectReport;
